import { useMemo } from "react";
import type { UseFormWatch } from "react-hook-form";
import type {
  NewFormData,
  MaintenanceNewFormData,
  PoolRegulationNewFormData,
  AdverseEventNewFormData,
} from "./RegisterNewForm.types";
import type {
  ReportType,
  MaintenanceType,
  InterventionLevel,
  AdverseEventType,
} from "../../../../api/client/types/catalog.types";
import type { Swimmer } from "../../../../api/client/types/swimmer.types";
import {
  isMaintenanceReportType,
  isPoolRegulationReportType,
  isAdverseEventReportType,
  isFirstAidStationReportType,
  REPORT_TYPE_NAMES,
  ADVERSE_EVENT_TYPE_NAMES,
  MAINTENANCE_TYPE_NAMES,
  findReportTypeByName,
  isAdverseEventTypeValue,
  extractAdverseEventTypeId,
  createAdverseEventTypeValue,
  isMaintenanceTypeValue,
  extractMaintenanceTypeId,
  createMaintenanceTypeValue,
  isLlamadoDeAtencionInterventionLevel,
} from "../../utils/reportTypeConstants";

/**
 * Report type names that should be excluded when a swimmer is present.
 * These report types are not associated with swimmers.
 */
const EXCLUDED_REPORT_TYPES_WITH_SWIMMER = [
  REPORT_TYPE_NAMES.MAINTENANCE,
  REPORT_TYPE_NAMES.FIRST_AID,
] as const;

/**
 * Report type names that should be excluded when a swimmer is not present.
 * These report types are associated with swimmers.
 */
const EXCLUDED_REPORT_TYPES_WITHOUT_SWIMMER = [
  REPORT_TYPE_NAMES.POOL_REGULATION,
] as const;

interface UseReportTypeLogicProps {
  reportTypes: ReportType[];
  maintenanceTypes: MaintenanceType[];
  interventionLevels: InterventionLevel[];
  adverseEventTypes: AdverseEventType[];
  swimmer?: Swimmer;
  watch: UseFormWatch<
    | NewFormData
    | MaintenanceNewFormData
    | PoolRegulationNewFormData
    | AdverseEventNewFormData
  >;
}

/**
 * Custom hook that handles all report type business logic and rules.
 * 
 * Responsibilities:
 * - Report type options generation
 * - Report type detection
 * - Adverse event type detection
 * - Progressive display logic
 * - Dynamic UI behavior (placeholders, textarea rows)
 */
export const useReportTypeLogic = ({
  reportTypes,
  maintenanceTypes,
  interventionLevels,
  adverseEventTypes,
  swimmer,
  watch,
}: UseReportTypeLogicProps) => {
  // Watch form values for business logic
  const reportTypeId = watch("report_type_id");
  const maintenanceTypeId = watch("maintenance_type_id");
  const poolRegulationId = watch("pool_regulation_id");
  const interventionLevelId = watch("intervention_level_id");

  // ============================================
  // REPORT TYPE OPTIONS GENERATION
  // ============================================

  /**
   * Filter report types and merge with maintenance types and adverse event types.
   * Both maintenance and adverse event types appear as independent options in the same radio group.
   * Only show options that are applicable based on swimmer presence.
   */
  const reportTypeOptions = useMemo(() => {
    // Get IDs of excluded report types for both cases
    const excludedWithSwimmerIds = EXCLUDED_REPORT_TYPES_WITH_SWIMMER.map(
      (typeName) => findReportTypeByName(reportTypes, typeName)
    )
      .filter((type): type is NonNullable<typeof type> => type !== undefined)
      .map((type) => type.id);

    const excludedWithoutSwimmerIds = EXCLUDED_REPORT_TYPES_WITHOUT_SWIMMER.map(
      (typeName) => findReportTypeByName(reportTypes, typeName)
    )
      .filter((type): type is NonNullable<typeof type> => type !== undefined)
      .map((type) => type.id);

    // Filter out "Eventos Adversos" report type and map other report types to options
    // Only include options that should be shown based on swimmer presence
    const regularReportTypeOptions = reportTypes
      .filter((reportType) => {
        // Filter out "Eventos Adversos"
        if (isAdverseEventReportType(reportType.id, reportTypes)) {
          return false;
        }

        // Filter based on swimmer presence
        const optionId = reportType.id;
        const isExcludedWithSwimmer = excludedWithSwimmerIds.includes(optionId);
        const isExcludedWithoutSwimmer =
          excludedWithoutSwimmerIds.includes(optionId);

        // Include if not excluded for current context
        return swimmer ? !isExcludedWithSwimmer : !isExcludedWithoutSwimmer;
      })
      .map((reportType) => ({
        label: reportType.name,
        value: reportType.id.toString(),
      }));

    // Add specific maintenance types as independent options (e.g., "Elementos de Emergencia")
    // These appear at the report type level instead of as sub-options under "Mantenimiento"
    // When swimmer exists: hide all maintenance types (they don't require swimmer)
    const maintenanceTypeOptions = swimmer
      ? []
      : maintenanceTypes
          .filter((maintenanceType) => {
            // Only show "Elementos de Emergencia" as a top-level option
            return maintenanceType.name === MAINTENANCE_TYPE_NAMES.EMERGENCY_ELEMENTS;
          })
          .map((maintenanceType) => ({
            label: maintenanceType.name,
            value: createMaintenanceTypeValue(maintenanceType.id),
          }));

    // Add adverse event types as independent options in the same radio group
    // When swimmer exists: show all except "Condiciones Meteorológicas"
    // When no swimmer: show only "Condiciones Meteorológicas"
    const adverseEventOptions = adverseEventTypes
      .filter((adverseEventType) => {
        const isMeteorologicalConditions =
          adverseEventType.name ===
          ADVERSE_EVENT_TYPE_NAMES.METEOROLOGICAL_CONDITIONS;

        // Include based on swimmer presence
        return swimmer
          ? !isMeteorologicalConditions
          : isMeteorologicalConditions;
      })
      .map((adverseEventType) => ({
        label: adverseEventType.name,
        value: createAdverseEventTypeValue(adverseEventType.id),
      }));

    // Combine all arrays - maintenance and adverse event types appear alongside regular report types
    return [...regularReportTypeOptions, ...maintenanceTypeOptions, ...adverseEventOptions];
  }, [reportTypes, maintenanceTypes, adverseEventTypes, swimmer]);

  // ============================================
  // REPORT TYPE DETECTION
  // ============================================

  // Check if maintenance report type is selected OR if a maintenance type value is selected
  const isMaintenanceSelected = useMemo(() => {
    if (!reportTypeId) return false;
    
    // Check if it's the regular "Mantenimiento" report type
    if (isMaintenanceReportType(reportTypeId, reportTypes)) {
      return true;
    }
    
    // Check if it's a maintenance type value (e.g., "maintenance_1" for "Elementos de Emergencia")
    return isMaintenanceTypeValue(reportTypeId);
  }, [reportTypeId, reportTypes]);

  // Check if "Elementos de Emergencia" is selected (maintenance type shown at report type level)
  const isEmergencyElementsSelected = useMemo(() => {
    if (!reportTypeId || !isMaintenanceTypeValue(reportTypeId)) {
      return false;
    }
    
    try {
      const maintenanceTypeId = extractMaintenanceTypeId(reportTypeId);
      const selectedType = maintenanceTypes.find(
        (type) => type.id.toString() === maintenanceTypeId
      );
      return selectedType?.name === MAINTENANCE_TYPE_NAMES.EMERGENCY_ELEMENTS;
    } catch (error) {
      console.error("Failed to extract maintenance type ID:", error);
      return false;
    }
  }, [reportTypeId, maintenanceTypes]);

  // Check if pool regulation report type is selected
  const isPoolRegulationSelected = useMemo(() => {
    return reportTypeId
      ? isPoolRegulationReportType(reportTypeId, reportTypes)
      : false;
  }, [reportTypeId, reportTypes]);

  // Check if first aid station report type is selected
  const isFirstAidStationSelected = useMemo(() => {
    return reportTypeId
      ? isFirstAidStationReportType(reportTypeId, reportTypes)
      : false;
  }, [reportTypeId, reportTypes]);

  // Check if intervention level is "Llamado de Atención 1" or "Llamado de Atención 2"
  // These intervention levels do NOT require security_officer_name (similar to First Aid Station)
  const isLlamadoDeAtencionSelected = useMemo(() => {
    if (!isPoolRegulationSelected) {
      return false;
    }
    
    return isLlamadoDeAtencionInterventionLevel(interventionLevelId, interventionLevels);
  }, [interventionLevelId, isPoolRegulationSelected, interventionLevels]);

  // Check if adverse event report type is selected
  const isAdverseEventSelected = useMemo(() => {
    if (!reportTypeId) return false;
    return isAdverseEventTypeValue(reportTypeId);
  }, [reportTypeId]);

  // ============================================
  // ADVERSE EVENT TYPE DETECTION
  // ============================================

  // Extract the actual adverse event type ID when an adverse event is selected
  const selectedAdverseEventTypeId = useMemo(() => {
    if (!reportTypeId || !isAdverseEventTypeValue(reportTypeId)) {
      return null;
    }

    try {
      return extractAdverseEventTypeId(reportTypeId);
    } catch (error) {
      console.error("Failed to extract adverse event type ID:", error);
      return null;
    }
  }, [reportTypeId]);

  // Check if the selected adverse event type is "Accidente" (requires spent_items)
  const isAccidentSelected = useMemo(() => {
    if (!selectedAdverseEventTypeId || !adverseEventTypes) return false;
    const selectedType = adverseEventTypes.find(
      (type) => type.id.toString() === selectedAdverseEventTypeId
    );
    return selectedType?.name === ADVERSE_EVENT_TYPE_NAMES.ACCIDENT;
  }, [selectedAdverseEventTypeId, adverseEventTypes]);

  const isIncidentSelected = useMemo(() => {
    if (!selectedAdverseEventTypeId || !adverseEventTypes) return false;
    const selectedType = adverseEventTypes.find(
      (type) => type.id.toString() === selectedAdverseEventTypeId
    );
    return selectedType?.name === ADVERSE_EVENT_TYPE_NAMES.INCIDENTS;
  }, [selectedAdverseEventTypeId, adverseEventTypes]);

  const isAccidentOrIncidentSelected = isAccidentSelected || isIncidentSelected;

  // Check if the selected adverse event type is "Condiciones Meteorológicas"
  const isMeteorologicalConditionsSelected = useMemo(() => {
    if (!selectedAdverseEventTypeId || !adverseEventTypes) return false;
    const selectedType = adverseEventTypes.find(
      (type) => type.id.toString() === selectedAdverseEventTypeId
    );
    return (
      selectedType?.name === ADVERSE_EVENT_TYPE_NAMES.METEOROLOGICAL_CONDITIONS
    );
  }, [selectedAdverseEventTypeId, adverseEventTypes]);

  // Check if the selected adverse event type is "Agresión Verbal/Física"
  const isVerbalPhysicalAggressionSelected = useMemo(() => {
    if (!selectedAdverseEventTypeId || !adverseEventTypes) return false;
    const selectedType = adverseEventTypes.find(
      (type) => type.id.toString() === selectedAdverseEventTypeId
    );
    return (
      selectedType?.name === ADVERSE_EVENT_TYPE_NAMES.VERBAL_PHYSICAL_AGGRESSION
    );
  }, [selectedAdverseEventTypeId, adverseEventTypes]);

  // Check if a non-meteorological adverse event is selected
  const isNonMeteorologicalAdverseEventSelected = useMemo(() => {
    return isAdverseEventSelected && !isMeteorologicalConditionsSelected;
  }, [isAdverseEventSelected, isMeteorologicalConditionsSelected]);

  // ============================================
  // PROGRESSIVE DISPLAY LOGIC
  // ============================================

  /**
   * Determine if type-specific section is filled.
   * This controls when to show the common fields section.
   */
  const isTypeSpecificSectionFilled = useMemo(() => {
    // If no report type selected, section is not filled
    if (!reportTypeId) return false;

    // First Aid Station has no type-specific fields
    if (isFirstAidStationSelected) return true;

    // Maintenance requires maintenance_type_id (unless it's Emergency Elements)
    if (isMaintenanceSelected) {
      return isEmergencyElementsSelected || !!maintenanceTypeId;
    }

    // Pool Regulation requires both pool_regulation_id and intervention_level_id
    if (isPoolRegulationSelected) {
      return !!poolRegulationId && !!interventionLevelId;
    }

    // Adverse Events: no type-specific fields block the common section.
    // Q1/Q2/Q3 appear independently at the end of the form.
    if (isAdverseEventSelected) return true;

    // For other report types, consider filled if report type is selected
    return true;
  }, [
    reportTypeId,
    isFirstAidStationSelected,
    isMaintenanceSelected,
    isEmergencyElementsSelected,
    maintenanceTypeId,
    isPoolRegulationSelected,
    poolRegulationId,
    interventionLevelId,
    isAdverseEventSelected,
  ]);

  // Show common fields section only after type-specific section is filled
  const showCommonFields = reportTypeId && isTypeSpecificSectionFilled;

  // ============================================
  // DYNAMIC UI BEHAVIOR
  // ============================================

  /**
   * Dynamic placeholder for event description based on selected adverse event type.
   */
  const eventDescriptionPlaceholder = useMemo(() => {
    if (isMeteorologicalConditionsSelected) {
      return "Debe incluir el número de bañistas al momento del cierre";
    }

    if (isVerbalPhysicalAggressionSelected) {
      return "Debe incluir: ¿Qué y cómo pasó? ¿Cuál fue su intervención? ¿Cómo finalizó el evento?";
    }

    if (isNonMeteorologicalAdverseEventSelected) {
      return `Debe incluir: ¿Qué y cómo pasó? ¿Qué atención prestó al usuario? ¿Cómo finalizó el evento? ${
        isAccidentSelected ? "¿Requirió Traslado Asistencial?" : ""
      }`;
    }

    return "Describa el evento...";
  }, [
    isMeteorologicalConditionsSelected,
    isVerbalPhysicalAggressionSelected,
    isNonMeteorologicalAdverseEventSelected,
    isAccidentSelected,
  ]);

  /**
   * Dynamic textarea rows to accommodate longer placeholder text on mobile.
   */
  const eventDescriptionRows = useMemo(() => {
    // More rows for non-meteorological adverse events (longest placeholder)
    if (isNonMeteorologicalAdverseEventSelected) {
      return 6;
    }
    // Medium rows for meteorological conditions and verbal/physical aggression
    if (isMeteorologicalConditionsSelected || isVerbalPhysicalAggressionSelected) {
      return 5;
    }
    // Default rows for other report types
    return 4;
  }, [
    isMeteorologicalConditionsSelected,
    isVerbalPhysicalAggressionSelected,
    isNonMeteorologicalAdverseEventSelected,
  ]);

  // ============================================
  // RETURN ALL BUSINESS LOGIC VALUES
  // ============================================

  return {
    // Report type options
    reportTypeOptions,
    
    // Report type detection
    isMaintenanceSelected,
    isEmergencyElementsSelected,
    isPoolRegulationSelected,
    isFirstAidStationSelected,
    isLlamadoDeAtencionSelected,
    isAdverseEventSelected,
    
    // Adverse event specific helpers
    selectedAdverseEventTypeId,
    isAccidentSelected,
    isIncidentSelected,
    isAccidentOrIncidentSelected,
    isMeteorologicalConditionsSelected,
    isVerbalPhysicalAggressionSelected,
    isNonMeteorologicalAdverseEventSelected,
    
    // Progressive display helpers
    isTypeSpecificSectionFilled,
    showCommonFields,
    
    // Event description dynamic helpers
    eventDescriptionPlaceholder,
    eventDescriptionRows,
  };
};
